import { LINKEDIN_AUTOMATION_STAGE_STATUSES } from './linkedinAutomationStageStatuses'
import { representCompanyUrl } from './linkedinUrls'
import { LinkedinSettingKey } from '../../recommendations/recommendationSettingsKeys'
import { LinkedinRecommendationKey, StageType } from '../../utils/enums'
import { StageDef } from '../automationMachineInterfaces'
import {
  getCurrentValueForCheckbox,
  saveCurrentValueForCheckbox,
  toggleCheckbox,
} from '../checkboxDetection'
import {
  defaultCantUndoFn,
  getCurrentValueFnFromKey,
  getErrorIsDisabledAndReasonFn,
  getIsFulfilledFnFromKey,
} from '../recommendationCardDisplayUtils'

// Settings property functions
const getCurrentValueFn = (key) =>
  getCurrentValueFnFromKey(key, (value) => (value ? 'On' : 'Off'))

const getIsFulfilledFn = (key) =>
  getIsFulfilledFnFromKey(key, (value) => !value)

const getCantUndoFn = () => defaultCantUndoFn

function getIsDisabledFn(settingsKey: string) {
  const fn = getErrorIsDisabledAndReasonFn(settingsKey)
  return (automationErrors) => fn(automationErrors).isDisabled
}
function getIsDisabledReasonFn(settingsKey: string) {
  const fn = getErrorIsDisabledAndReasonFn(settingsKey)
  return (automationErrors) => fn(automationErrors).isDisabledReason
}

// Automations

enum StageKey {
  LINKEDIN_REPRESENT_COMPANY_GATHER = 'LINKEDIN_REPRESENT_COMPANY_GATHER',
  LINKEDIN_REPRESENT_COMPANY_UPDATE = 'LINKEDIN_REPRESENT_COMPANY_UPDATE',
  LINKEDIN_REPRESENT_COMPANY_UNDO = 'LINKEDIN_REPRESENT_COMPANY_UNDO',
}

const REPRESENT_COMPANY_SETTINGS = {
  REPRESENT_COMPANY: 'Profile information on content',
}

const gatherStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: StageKey.LINKEDIN_REPRESENT_COMPANY_GATHER,
    status: LINKEDIN_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    url: representCompanyUrl,
    action: async (machineContext, userContext) => {
      await saveCurrentValueForCheckbox(
        userContext.updateStoredAutomationErrors,
        userContext.updateStoredInitialValues,
        REPRESENT_COMPANY_SETTINGS,
        LinkedinSettingKey.REPRESENT_COMPANY,
        LinkedinRecommendationKey.LINKEDIN_REPRESENT_COMPANY,
        'p',
      )
    },
  },
]

const updateStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: StageKey.LINKEDIN_REPRESENT_COMPANY_UPDATE,
    status: LINKEDIN_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: representCompanyUrl,
    action: async (machineContext, userContext) => {
      const { isDry } = userContext

      const currentSetting = await getCurrentValueForCheckbox(
        REPRESENT_COMPANY_SETTINGS.REPRESENT_COMPANY,
        'p',
      )

      // Toggle off if currently on
      if (currentSetting && !isDry) {
        return toggleCheckbox(REPRESENT_COMPANY_SETTINGS.REPRESENT_COMPANY, 'p')
      }
      return null
    },
  },
]

const undoStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: StageKey.LINKEDIN_REPRESENT_COMPANY_UNDO,
    status: LINKEDIN_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: representCompanyUrl,
    action: async (machineContext, userContext) => {
      const { initialValues, isDry } = userContext

      const currentSetting = await getCurrentValueForCheckbox(
        REPRESENT_COMPANY_SETTINGS.REPRESENT_COMPANY,
        'p',
      )

      // Toggle setting if checkbox does not match initial values
      if (
        initialValues?.[LinkedinSettingKey.REPRESENT_COMPANY] !==
          currentSetting &&
        !isDry
      ) {
        return toggleCheckbox(
          REPRESENT_COMPANY_SETTINGS[LinkedinSettingKey.REPRESENT_COMPANY],
          'p',
        )
      }
      return null
    },
  },
]

export {
  getCantUndoFn,
  getCurrentValueFn,
  getIsDisabledFn,
  getIsDisabledReasonFn,
  getIsFulfilledFn,
  gatherStageDefs,
  updateStageDefs,
  undoStageDefs,
}
